"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import { cn } from "@/lib/utils"
import { QUALITY_EVAL_LOAD, QUALITY_PARTICIPATION } from "@/lib/mock/evaluation-quality-dashboard"

export function QualityParticipationBlock() {
  const p = QUALITY_PARTICIPATION

  return (
    <section aria-labelledby="quality-participation-heading" className="flex flex-col gap-4">
      <DashboardSectionTitle as="h2" id="quality-participation-heading">
        Evaluation participation
      </DashboardSectionTitle>

      <div className="rounded-xl border border-border/60 bg-card p-4">
        <p className="text-3xl font-bold tabular-nums text-foreground">{p.rate}%</p>
        <p className="mt-1 text-sm tabular-nums text-muted-foreground">
          {p.responded.toLocaleString()} / {p.total.toLocaleString()} responses
        </p>

        <ul className="mt-4 flex flex-wrap gap-3 text-sm">
          {p.buckets.map(bucket => (
            <li
              key={bucket.label}
              className={cn(
                "rounded-md border border-border/60 px-2.5 py-1 tabular-nums",
                bucket.alert && "border-chart-4/40 text-[color:var(--chart-4)]",
              )}
            >
              {bucket.label}: {bucket.count}
              {bucket.alert ? " ⚠" : ""}
            </li>
          ))}
        </ul>

        {p.lowResponse.length > 0 ? (
          <div className="mt-4 rounded-lg border border-chart-4/30 bg-chart-4/5 p-3 text-sm">
            <p className="font-medium text-foreground">
              {p.lowResponse.length} evaluations may not reach reporting threshold
            </p>
            {p.lowResponse.map(row => (
              <p key={row.name} className="mt-1 text-muted-foreground">
                {row.name} · {row.rate}% · {row.closesLabel}
              </p>
            ))}
            <Button size="sm" className="mt-3">
              Review response plan
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  )
}

export function QualityEvaluationLoadBlock() {
  const load = QUALITY_EVAL_LOAD
  const maxWeek = Math.max(...load.weeks.map(week => week.value), 1)

  return (
    <section aria-labelledby="quality-load-heading" className="flex flex-col gap-4">
      <DashboardSectionTitle as="h2" id="quality-load-heading">
        Evaluation load
      </DashboardSectionTitle>

      <div className="rounded-xl border border-border/60 bg-card p-4">
        <p className="text-sm text-muted-foreground">Average this week</p>
        <p className="text-3xl font-bold tabular-nums text-foreground">
          {load.averageThisWeek.toFixed(1)} evaluations
        </p>
        <p className="text-sm text-muted-foreground">~{load.minutesPerLearner} min / learner</p>

        <div className="mt-4 flex items-end gap-2" role="img" aria-label="Weekly evaluation load">
          {load.weeks.map(week => (
            <div key={week.label} className="flex flex-1 flex-col items-center gap-1">
              <div
                className={cn(
                  "w-full rounded-sm",
                  week.alert ? "bg-chart-4" : "bg-brand",
                )}
                style={{ height: `${Math.max(12, (week.value / maxWeek) * 48)}px` }}
              />
              <span className="text-xs text-muted-foreground">{week.label}</span>
            </div>
          ))}
        </div>

        <p className="mt-4 text-sm text-[color:var(--chart-4)]">{load.alert}</p>
        <Button size="sm" variant="outline" className="mt-3">
          Redistribute evaluations
        </Button>
      </div>
    </section>
  )
}
